/**
 * Icon3D
 *
 * Renders a premium 3D icon asset from `/icons/3d/{name}.png` with
 * graceful fallback to a flat lucide-react icon when the 3D asset is
 * missing (preview environments) or fails to load. Adds a subtle floating
 * hover animation that matches the rest of the design system.
 *
 * Usage:
 *   <Icon3D name="dashboard" size={40} />
 *   <Icon3D name="video" size={48} fallback={Video} />
 *
 * The component is intentionally lightweight — no JS animation, no
 * runtime CSS injection. All hover motion lives in index.css under
 * the `.icon-3d` selectors.
 */
import { useState } from "react";

const BASE = "/icons/3d";

export function Icon3D({
    name,
    size = 40,
    className = "",
    fallback: Fallback,
    alt = "",
    float = false,
    testId,
}) {
    const [errored, setErrored] = useState(false);

    if (errored && Fallback) {
        return (
            <span
                className={`icon-3d-fallback inline-flex items-center justify-center ${className}`}
                style={{ width: size, height: size }}
                data-testid={testId}
            >
                <Fallback className="h-1/2 w-1/2" strokeWidth={1.6} />
            </span>
        );
    }

    return (
        <img
            src={`${BASE}/${name}.png`}
            alt={alt}
            width={size}
            height={size}
            loading="lazy"
            decoding="async"
            onError={() => setErrored(true)}
            className={`icon-3d ${float ? "icon-3d-float" : ""} ${className}`}
            style={{ width: size, height: size }}
            data-testid={testId}
        />
    );
}
